import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import {
  Button,
  confirm,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  GripVertical,
  MessageSquareIcon,
  PlusIcon,
  Settings,
  SparklesIcon,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import { nanoid } from "nanoid";
import React from "react";
import { z } from "zod";
import {
  CreateServiceStandardFormSchema,
  ServiceStandardExampleFormSchema,
  ServiceStandardItemFormSchema,
  ServiceStandardSpecialCaseFormSchema,
} from "../../domain/contracts";
import type { ServiceChannelDTO } from "../api/client";
import {
  useCreateServiceChannel,
  useDeleteServiceChannel,
  useServiceChannels,
  useUpdateServiceChannel,
} from "../api/queries";
import { ServiceChannelForm } from "./ServiceChannelForm";
import { serviceStandardFormSamples } from "./serviceStandardFormSamples";

const ServiceStandardFormSchema = CreateServiceStandardFormSchema.extend({
  items: z.array(ServiceStandardItemFormSchema).optional().default([]),
  examples: z.array(ServiceStandardExampleFormSchema).optional().default([]),
  specialCases: z
    .array(ServiceStandardSpecialCaseFormSchema)
    .optional()
    .default([]),
});

export type ServiceStandardFormValues = z.infer<
  typeof ServiceStandardFormSchema
>;

type ServiceChannelItem = LookupItem;
type DepartmentItem = LookupItem;
type PositionItem = LookupItem;

const serviceStageOptions = [
  { value: "before", label: "ກ່ອນບໍລິການ" },
  { value: "during", label: "ລະຫວ່າງບໍລິການ" },
  { value: "after", label: "ຫຼັງບໍລິການ" },
  { value: "all", label: "ທຸກເວລາ" },
];

const statusOptions = [
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "draft", label: "ປັບປຸງ" },
  { value: "inactive", label: "ເລີກໃຊ້" },
];

// Sortable Item Component
function SortableItemItem({
  itemIndex,
  itemType,
  onDelete,
}: {
  itemIndex: number;
  itemType: "do" | "dont";
  onDelete: () => void;
}) {
  const items = RHF.useWatch({ name: "items" }) || [];
  const item = items[itemIndex];

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item?.id || `item-${itemIndex}`,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!item) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 rounded-lg border bg-card p-4"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          {itemType === "do" ? (
            <CheckCircle2Icon className="h-4 w-4 text-green-600" />
          ) : (
            <XCircleIcon className="h-4 w-4 text-red-600" />
          )}
          <span className="font-medium text-sm">#{itemIndex + 1}</span>
        </div>
        <FormInput
          name={`items.${itemIndex}.content`}
          placeholder="ກະລຸນາໃສ່ເນື້ອໃນ"
          className="w-full"
        />
        <FormTextarea
          name={`items.${itemIndex}.description`}
          placeholder="ຄຳອະທິບາຍເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
          rows={2}
          className="w-full"
        />
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="mt-1 text-destructive hover:text-destructive/80"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// Sortable Example Component
function SortableExampleItem({
  exampleIndex,
  onDelete,
}: {
  exampleIndex: number;
  onDelete: () => void;
}) {
  const examples = RHF.useWatch({ name: "examples" }) || [];
  const example = examples[exampleIndex];

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: example?.id || `example-${exampleIndex}`,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!example) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 rounded-lg border bg-card p-4"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <MessageSquareIcon className="h-4 w-4 text-blue-600" />
          <span className="font-medium text-sm">#{exampleIndex + 1}</span>
        </div>
        <FormInput
          name={`examples.${exampleIndex}.situation`}
          placeholder="ສະຖານະການ (ຕົວຢ່າງ: ທັກທາຍ, ຕອບແຊັດ)"
          className="w-full"
        />
        <FormTextarea
          name={`examples.${exampleIndex}.example`}
          placeholder="ຕົວຢ່າງປະໂຫຍກ"
          rows={2}
          className="w-full"
        />
        <FormTextarea
          name={`examples.${exampleIndex}.description`}
          placeholder="ຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={1}
          className="w-full"
        />
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="mt-1 text-destructive hover:text-destructive/80"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// Sortable Special Case Component
function SortableSpecialCaseItem({
  specialCaseIndex,
  onDelete,
}: {
  specialCaseIndex: number;
  onDelete: () => void;
}) {
  const specialCases = RHF.useWatch({ name: "specialCases" }) || [];
  const specialCase = specialCases[specialCaseIndex];

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: specialCase?.id || `special-case-${specialCaseIndex}`,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!specialCase) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 rounded-lg border bg-card p-4"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-1 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <AlertTriangleIcon className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-sm">#{specialCaseIndex + 1}</span>
        </div>
        <FormInput
          name={`specialCases.${specialCaseIndex}.title`}
          placeholder="ຫົວຂໍ້ (ຕົວຢ່າງ: ລູກຄ້າບໍ່ພໍໃຈ)"
          className="w-full"
        />
        <FormTextarea
          name={`specialCases.${specialCaseIndex}.handling`}
          placeholder="ວິທີການຈັດການ"
          rows={2}
          className="w-full"
        />
        <FormTextarea
          name={`specialCases.${specialCaseIndex}.description`}
          placeholder="ຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={1}
          className="w-full"
        />
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="mt-1 text-destructive hover:text-destructive/80"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export function ServiceStandardForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<ServiceStandardFormValues>;
  onSubmit: (values: ServiceStandardFormValues) => void;
  submitting?: boolean;
}) {
  const [sampleIndex, setSampleIndex] = React.useState(0);
  const isDev =
    import.meta.hot !== undefined || import.meta.env?.MODE === "development";

  const methods = RHF.useForm<ServiceStandardFormValues>({
    resolver: zodResolver(ServiceStandardFormSchema) as any,
    defaultValues: {
      name: "",
      description: null,
      serviceChannelId: "",
      departmentId: null,
      positionId: null,
      serviceStage: "all",
      status: "draft",
      items: [],
      examples: [],
      specialCases: [],
      ...initialValues,
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const itemsFieldArray = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });

  const examplesFieldArray = RHF.useFieldArray({
    control: methods.control,
    name: "examples",
  });

  const specialCasesFieldArray = RHF.useFieldArray({
    control: methods.control,
    name: "specialCases",
  });

  const items = methods.watch("items") || [];
  const examples = methods.watch("examples") || [];
  const specialCases = methods.watch("specialCases") || [];

  const doItems = items.filter((item) => item.itemType === "do");
  const dontItems = items.filter((item) => item.itemType === "dont");

  // Map to original indices
  const getDoItemOriginalIndex = (doIndex: number) => {
    return items.findIndex((item) => {
      if (item.itemType === "do") {
        return doItems.indexOf(item) === doIndex;
      }
      return false;
    });
  };

  const getDontItemOriginalIndex = (dontIndex: number) => {
    return items.findIndex((item) => {
      if (item.itemType === "dont") {
        return dontItems.indexOf(item) === dontIndex;
      }
      return false;
    });
  };

  const handleDragEndItems = (event: DragEndEvent, itemType: "do" | "dont") => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentItems = itemType === "do" ? doItems : dontItems;
    const oldIndex = currentItems.findIndex((item, idx) => {
      const origIdx =
        itemType === "do"
          ? getDoItemOriginalIndex(idx)
          : getDontItemOriginalIndex(idx);
      return (item.id || `item-${origIdx}`) === active.id;
    });
    const newIndex = currentItems.findIndex((item) => {
      const itemIdx = currentItems.indexOf(item);
      const origIdx =
        itemType === "do"
          ? getDoItemOriginalIndex(itemIdx)
          : getDontItemOriginalIndex(itemIdx);
      return (item.id || `item-${origIdx}`) === over.id;
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      const oldOrigIndex =
        itemType === "do"
          ? getDoItemOriginalIndex(oldIndex)
          : getDontItemOriginalIndex(oldIndex);
      const newOrigIndex =
        itemType === "do"
          ? getDoItemOriginalIndex(newIndex)
          : getDontItemOriginalIndex(newIndex);

      itemsFieldArray.move(oldOrigIndex, newOrigIndex);

      // Update orderIndex
      const updatedItems = methods.getValues("items");
      const filtered = updatedItems.filter(
        (item) => item.itemType === itemType,
      );
      filtered.forEach((item, idx) => {
        const origIdx = updatedItems.indexOf(item);
        methods.setValue(`items.${origIdx}.orderIndex`, idx);
      });
    }
  };

  const handleDragEndExamples = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = examples.findIndex(
      (ex, idx) => (ex.id || `example-${idx}`) === active.id,
    );
    const newIndex = examples.findIndex((ex) => {
      const idx = examples.indexOf(ex);
      return (ex.id || `example-${idx}`) === over.id;
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      examplesFieldArray.move(oldIndex, newIndex);

      // Update orderIndex
      examples.forEach((_ex, idx) => {
        methods.setValue(`examples.${idx}.orderIndex`, idx);
      });
    }
  };

  const handleDragEndSpecialCases = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = specialCases.findIndex(
      (sc, idx) => (sc.id || `special-case-${idx}`) === active.id,
    );
    const newIndex = specialCases.findIndex((sc) => {
      const idx = specialCases.indexOf(sc);
      return (sc.id || `special-case-${idx}`) === over.id;
    });

    if (oldIndex !== -1 && newIndex !== -1) {
      specialCasesFieldArray.move(oldIndex, newIndex);

      // Update orderIndex
      specialCases.forEach((_sc, idx) => {
        methods.setValue(`specialCases.${idx}.orderIndex`, idx);
      });
    }
  };

  const handleAddDoItem = () => {
    itemsFieldArray.append({
      id: nanoid(),
      itemType: "do" as const,
      content: "",
      description: null,
      orderIndex: doItems.length,
    });
  };

  const handleAddDontItem = () => {
    itemsFieldArray.append({
      id: nanoid(),
      itemType: "dont" as const,
      content: "",
      description: null,
      orderIndex: dontItems.length,
    });
  };

  const handleAddExample = () => {
    examplesFieldArray.append({
      id: nanoid(),
      situation: "",
      example: "",
      description: null,
      orderIndex: examples.length,
    });
  };

  const handleAddSpecialCase = () => {
    specialCasesFieldArray.append({
      id: nanoid(),
      title: "",
      handling: "",
      description: null,
      orderIndex: specialCases.length,
    });
  };

  const handleDeleteItem = (index: number, itemType: "do" | "dont") => {
    const filtered = items.filter((item) => item.itemType === itemType);
    const itemToDelete = filtered[index];
    if (itemToDelete) {
      const origIndex = items.indexOf(itemToDelete);
      itemsFieldArray.remove(origIndex);
    }
  };

  const handleDeleteExample = (index: number) => {
    examplesFieldArray.remove(index);
  };

  const handleDeleteSpecialCase = (index: number) => {
    specialCasesFieldArray.remove(index);
  };

  const handleAutoFill = () => {
    const sample = serviceStandardFormSamples[sampleIndex];
    if (sample) {
      Object.entries(sample).forEach(([key, value]) => {
        methods.setValue(
          key as keyof ServiceStandardFormValues,
          value as never,
        );
      });
      setSampleIndex((prev) => (prev + 1) % serviceStandardFormSamples.length);
    }
  };

  // Service Channel Management Component
  function ServiceChannelManagementButton() {
    const canManage = useActionPermission([
      "service_standard:create",
      "service_standard:update",
      "service_standard:delete",
    ]);
    const listModal = useDisclosure();
    const createModal = useDisclosure();
    const editModal = useDisclosure<ServiceChannelDTO>();
    const listQuery = useServiceChannels({ limit: 100, offset: 0 });
    const createServiceChannel = useCreateServiceChannel();
    const updateServiceChannel = useUpdateServiceChannel();
    const deleteServiceChannel = useDeleteServiceChannel();

    if (!canManage) return null;

    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => listModal.open()}
          title="ຈັດການຊ່ອງທາງບໍລິການ"
          className="mt-6"
        >
          <Settings className="h-4 w-4" />
        </Button>

        {/* Service Channels List Modal */}
        <Modal
          open={!!listModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              listModal.close();
              createModal.close();
              editModal.close();
            }
          }}
          title="ຈັດການຊ່ອງທາງບໍລິການ"
          description="ສ້າງ, ແກ້ໄຂ, ຫຼື ລຶບຊ່ອງທາງບໍລິການ"
          size="md"
        >
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  listModal.close();
                  createModal.open();
                }}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                ສ້າງຊ່ອງທາງບໍລິການໃໝ່
              </Button>
            </div>

            {listQuery.isLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                ກຳລັງໂຫຼດ...
              </div>
            ) : listQuery.data?.data.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                ຍັງບໍ່ມີຊ່ອງທາງບໍລິການ
              </div>
            ) : (
              <div className="space-y-2">
                {listQuery.data?.data.map((channel) => (
                  <div
                    key={channel.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{channel.name}</span>
                      {channel.description && (
                        <span className="text-muted-foreground text-xs">
                          {channel.description}
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        ລໍາດັບ: {channel.orderIndex}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          listModal.close();
                          editModal.openWith(channel);
                        }}
                      >
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຊ່ອງທາງບໍລິການ "${channel.name}"?`,
                              description: "ຊ່ອງທາງບໍລິການນີ້ຈະລຶບອອກຈາກລາຍການທັງຫມົດ",
                              actionText: "ລຶບ",
                              ActionProps: {
                                variant: "destructive",
                              },
                            })
                          ) {
                            await deleteServiceChannel.mutateAsync(channel.id);
                          }
                        }}
                      >
                        ລຶບ
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Create Service Channel Modal */}
        <Modal
          open={!!createModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              createModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ສ້າງຊ່ອງທາງບໍລິການ"
          description="ກໍານົດຊື່, ຄໍາອະທິບາຍ, ແລະລໍາດັບຂອງຊ່ອງທາງບໍລິການ."
          size="sm"
        >
          <ServiceChannelForm
            onSubmit={async (vals) => {
              await createServiceChannel.mutateAsync({
                name: vals.name,
                description: vals.description ?? null,
                orderIndex: vals.orderIndex ?? 0,
              });
              createModal.close();
              listModal.open();
            }}
            submitting={createServiceChannel.isPending}
          />
        </Modal>

        {/* Edit Service Channel Modal */}
        <Modal
          open={!!editModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              editModal.close();
            } else {
              listModal.close();
            }
          }}
          title="ແກ້ໄຂຊ່ອງທາງບໍລິການ"
          description="ປັບປຸງລາຍລະອຽດຂອງຊ່ອງທາງບໍລິການ."
          size="sm"
        >
          <ServiceChannelForm
            initialValues={
              editModal.data
                ? {
                    name: editModal.data.name,
                    description: editModal.data.description,
                    orderIndex: editModal.data.orderIndex,
                  }
                : undefined
            }
            onSubmit={async (vals) => {
              if (!editModal.data) return;
              await updateServiceChannel.mutateAsync({
                id: editModal.data.id,
                input: {
                  name: vals.name,
                  description: vals.description ?? null,
                  orderIndex: vals.orderIndex ?? 0,
                },
              });
              editModal.close();
              listModal.open();
            }}
            submitting={updateServiceChannel.isPending}
          />
        </Modal>
      </>
    );
  }

  const handleSubmit = (status: "draft" | "active") => {
    methods.handleSubmit((values: ServiceStandardFormValues) => {
      onSubmit({ ...values, status });
    })();
  };

  return (
    <FormRoot<ServiceStandardFormValues>
      methods={methods}
      onSubmit={(values) => onSubmit(values)}
      className="space-y-6"
    >
      {/* Section 1: ข้อมูลมาตรฐาน */}
      <div className="space-y-4 rounded-lg border bg-card p-6">
        <h2 className="font-semibold text-lg">ສ່ວນທີ 1: ຂໍ້ມູນມາດຕະຖານ</h2>
        <div data-tourid="form-name">
          <FormInput
            name="name"
            label="ຊື່ມາດຕະຖານ"
            requiredMark
            placeholder="ຕົວຢ່າງ: ມາດຕະຖານການຕ້ອນຮັບລູກຄ້າ"
          />
        </div>
        <div data-tourid="form-description">
          <FormTextarea
            name="description"
            label="ຄຳອະທິບາຍ"
            placeholder="ອະທິບາຍເພີ່ມເຕີມ (ບໍ່ບັງຄັບ)"
            rows={3}
          />
        </div>
        <div
          className="flex items-end gap-2"
          data-tourid="form-service-channel"
        >
          <FormInfiniteCombobox<ServiceChannelItem>
            queryKey={["service-channels", "form"]}
            name="serviceChannelId"
            label="ເລືອກຊ່ອງທາງບໍລິການ"
            requiredMark
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/service-standard/service-channels/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/service-standard/service-channels/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຊ່ອງທາງບໍລິການ"
            className="flex-1"
          />
          <ServiceChannelManagementButton />
        </div>
        <div data-tourid="form-department">
          <FormInfiniteCombobox<DepartmentItem>
            name="departmentId"
            label="ເລືອກພະແນກ"
            queryKey={["service-standard-departments", "form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/service-standard/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/service-standard/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ (ບໍ່ບັງຄັບ)"
            clearable
          />
        </div>
        <div data-tourid="form-position">
          <FormInfiniteCombobox<PositionItem>
            name="positionId"
            label="ເລືອກຕໍາແໜ່ງ"
            queryKey={["service-standard-positions", "form"]}
            queryFn={(args) =>
              fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກຕໍາແໜ່ງ (ບໍ່ບັງຄັບ - ຖ້າບໍ່ເລືອກ = ໃຊ້ທຸກຕໍາແໜ່ງ)"
            clearable
          />
        </div>
        <div data-tourid="form-service-stage">
          <FormSelect
            name="serviceStage"
            label="ໃຊ້ເວລາໃດ"
            options={serviceStageOptions}
          />
        </div>
        <div data-tourid="form-status">
          <FormSelect name="status" label="ສະຖານະ" options={statusOptions} />
        </div>
      </div>

      {/* Section 2: สิ่งที่ต้องทำ */}
      <div
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="form-do-items"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className="h-5 w-5 text-green-600" />
            <h2 className="font-semibold text-lg">ສ່ວນທີ 2: ສິ່ງທີ່ຕ້ອງເຮັດ</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddDoItem}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມລາຍການ
          </Button>
        </div>
        {doItems.length === 0 ? (
          <p className="text-muted-foreground text-sm italic">
            ຍັງບໍ່ມີລາຍການ. ກົດປຸ່ມ "ເພີ່ມລາຍການ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleDragEndItems(e, "do")}
          >
            <SortableContext
              items={doItems.map(
                (item) => item.id || `item-${items.indexOf(item)}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {doItems.map((_item, index) => {
                  const origIndex = getDoItemOriginalIndex(index);
                  return (
                    <SortableItemItem
                      key={
                        itemsFieldArray.fields[origIndex]?.id ||
                        `do-item-${index}`
                      }
                      itemIndex={origIndex}
                      itemType="do"
                      onDelete={() => handleDeleteItem(index, "do")}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Section 3: สิ่งที่ห้ามทำ */}
      <div
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="form-dont-items"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <XCircleIcon className="h-5 w-5 text-red-600" />
            <h2 className="font-semibold text-lg">ສ່ວນທີ 3: ສິ່ງທີ່ຫ້າມເຮັດ</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddDontItem}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມລາຍການ
          </Button>
        </div>
        {dontItems.length === 0 ? (
          <p className="text-muted-foreground text-sm italic">
            ຍັງບໍ່ມີລາຍການ. ກົດປຸ່ມ "ເພີ່ມລາຍການ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleDragEndItems(e, "dont")}
          >
            <SortableContext
              items={dontItems.map(
                (item) => item.id || `item-${items.indexOf(item)}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {dontItems.map((_item, index) => {
                  const origIndex = getDontItemOriginalIndex(index);
                  return (
                    <SortableItemItem
                      key={
                        itemsFieldArray.fields[origIndex]?.id ||
                        `dont-item-${index}`
                      }
                      itemIndex={origIndex}
                      itemType="dont"
                      onDelete={() => handleDeleteItem(index, "dont")}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Section 4: ตัวอย่างการพูด */}
      <div
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="form-examples"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquareIcon className="h-5 w-5 text-blue-600" />
            <h2 className="font-semibold text-lg">ສ່ວນທີ 4: ຕົວຢ່າງການເວົ້າ</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddExample}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມຕົວຢ່າງ
          </Button>
        </div>
        {examples.length === 0 ? (
          <p className="text-muted-foreground text-sm italic">
            ຍັງບໍ່ມີຕົວຢ່າງ. ກົດປຸ່ມ "ເພີ່ມຕົວຢ່າງ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEndExamples}
          >
            <SortableContext
              items={examples.map(
                (ex) => ex.id || `example-${examples.indexOf(ex)}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {examplesFieldArray.fields.map((field, index) => (
                  <SortableExampleItem
                    key={field.id}
                    exampleIndex={index}
                    onDelete={() => handleDeleteExample(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Section 5: กรณีพิเศษ */}
      <div
        className="space-y-4 rounded-lg border bg-card p-6"
        data-tourid="form-special-cases"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangleIcon className="h-5 w-5 text-amber-600" />
            <h2 className="font-semibold text-lg">ສ່ວນທີ 5: ກໍລະນີພິເສດ</h2>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddSpecialCase}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            ເພີ່ມກໍລະນີພິເສດ
          </Button>
        </div>
        {specialCases.length === 0 ? (
          <p className="text-muted-foreground text-sm italic">
            ຍັງບໍ່ມີກໍລະນີພິເສດ. ກົດປຸ່ມ "ເພີ່ມກໍລະນີພິເສດ" ເພື່ອເລີ່ມຕົ້ນ
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEndSpecialCases}
          >
            <SortableContext
              items={specialCases.map(
                (sc) => sc.id || `special-case-${specialCases.indexOf(sc)}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3">
                {specialCasesFieldArray.fields.map((field, index) => (
                  <SortableSpecialCaseItem
                    key={field.id}
                    specialCaseIndex={index}
                    onDelete={() => handleDeleteSpecialCase(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Form Actions */}
      {/* <div className="flex items-center justify-end gap-2"> */}
      <div
        className="sticky bottom-2 flex justify-between border-t bg-background pt-4"
        data-tourid="form-submit"
      >
        {isDev && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="mr-2 h-4 w-4" />
            Auto Fill (Sample {sampleIndex + 1}/
            {serviceStandardFormSamples.length})
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={submitting}
          >
            ບັນທຶກເປັນຮ່າງ
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit("active")}
            disabled={submitting}
          >
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກແລະເປີດໃຊ້ງານ"}
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
